import { Body, Panel, PanelProps } from '@hover/blueprint';
import React, { useState } from 'react';
import { animated, config, useTransition } from 'react-spring';
import styled from 'styled-components';

import { useIsInitialRender } from '../../hooks';

type AlertColor = 'success' | 'danger' | 'warning';

interface AlertProps extends Omit<PanelProps, 'color'> {
  color?: AlertColor;
}

const AnimatedBody = animated(Body);

const StyledPanel = styled(Panel)`
  overflow: hidden;
  position: relative;
  transition-duration: 250ms;
  transition-property: background-color;
  transition-timing-function: ease;
`;

const themes: {
  [key in AlertColor]: Pick<PanelProps, 'color' | 'backgroundColor'>;
} = {
  danger: { color: 'danger800', backgroundColor: 'danger100' },
  success: { color: 'success800', backgroundColor: 'success100' },
  warning: { color: 'warning800', backgroundColor: 'warning100' },
} as const;

const Alert = ({ color = 'danger', children, ...props }: AlertProps) => {
  // All of this transition logic enables transitions between *string-based*
  // children, i.e: when content of the `Alert` changes we transition between
  // those messages. This does not currently work for `children` when it is not
  // a string because we're currently comparing `children` by reference.

  const swapper = [children];

  const [childrens, set] = useState(() => swapper);
  const transitions = useTransition(
    childrens,
    item => (item === children ? 1 : 0),
    {
      immediate: useIsInitialRender(),
      config: config.stiff,
      enter: { opacity: 1, transform: 'translate3d(0,0px,0)' },
      from: { opacity: 0, transform: 'translate3d(0,-100%,0)' },
      leave: {
        opacity: 0,
        position: 'absolute',
        transform: 'translate3d(0,100%,0)',
        width: '100%',
      },
    },
  );

  if (childrens[0] !== children) set(swapper);

  return (
    <StyledPanel
      display="block"
      {...props}
      {...themes[color]}
      aria-live="assertive"
      role="alert"
    >
      {typeof children === 'string'
        ? transitions.map(({ item, key, props: style }) => (
            <AnimatedBody
              color="inherit"
              key={key}
              margin={0}
              size={400}
              style={style}
            >
              {item}
            </AnimatedBody>
          ))
        : children}
    </StyledPanel>
  );
};

export { Alert };
